import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, ImagePlus, Loader2 } from "lucide-react";
import { http, formatApiError, imgSrc } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useSite } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EnquiriesPanel = () => {
  const [items, setItems] = useState(null);
  const load = () => http.get("/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await http.patch(`/enquiries/${id}`, { status });
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const remove = async (id) => {
    await http.delete(`/enquiries/${id}`);
    setItems((xs) => xs.filter((x) => x.id !== id));
    toast.success("Enquiry removed");
  };

  if (!items) return <p className="text-gw-ground/70">Loading...</p>;
  if (!items.length) return <p className="py-10 text-gw-ground/70" data-testid="admin-enquiries-empty">No enquiries yet.</p>;
  return (
    <div className="border-t border-gw-ground/20" data-testid="admin-enquiries-list">
      {items.map((q, i) => (
        <div key={q.id} className="py-5 border-b border-gw-ground/20 grid md:grid-cols-[1fr_auto] gap-4" data-testid={`admin-enquiry-row-${i}`}>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <p className="lux text-xl">{q.name}</p>
              <span className="lab text-[10px] bg-gw-tint px-2 py-0.5">{q.company}</span>
              {q.ref_title && <span className="lab text-[10px] border border-gw-ground/30 px-2 py-0.5">Re: {q.ref_title}</span>}
              {q.email_status && (
                <span className={`lab text-[10px] px-2 py-0.5 ${q.email_status === "sent" ? "bg-gw-ground text-white" : "bg-amber-50 text-amber-800 border border-amber-200"}`}
                  title={q.emailed_to || ""} data-testid={`admin-enquiry-email-status-${i}`}>
                  {EMAIL_STATUS[q.email_status] || q.email_status}
                </span>
              )}
              {q.auto_reply_status === "sent" && (
                <span className="lab text-[10px] px-2 py-0.5 border border-gw-ground/30" data-testid={`admin-enquiry-autoreply-${i}`}>Auto-reply sent</span>
              )}
            </div>
            <p className="text-sm text-gw-ground/70 mt-1">
              <a href={`mailto:${q.email}`} className="underline underline-offset-2">{q.email}</a>
              {q.phone && <> · <a href={`tel:${q.phone}`}>{q.phone}</a></>} · {new Date(q.created_at).toLocaleString()}
            </p>
            <p className="mt-3 whitespace-pre-line">{q.message}</p>
          </div>
          <div className="flex items-start gap-2">
            <Select value={q.status} onValueChange={(v) => setStatus(q.id, v)}>
              <SelectTrigger className="w-36 rounded-none" data-testid={`admin-enquiry-status-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {["New", "Contacted", "Closed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <a href={`mailto:${q.email}?subject=${encodeURIComponent(`Re: your enquiry${q.ref_title ? ` about ${q.ref_title}` : ""}`)}`}
              aria-label="Reply by email" className="p-2.5 border border-gw-ground/20 hover:bg-gw-tint transition-colors duration-200" data-testid={`admin-enquiry-reply-${i}`}><Mail size={16} /></a>
            <button type="button" aria-label="Delete" onClick={() => remove(q.id)} className="p-2.5 border border-gw-ground/20 text-destructive hover:bg-red-50 transition-colors duration-200" data-testid={`admin-enquiry-delete-${i}`}><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

const NOTIFY_FIELDS = [
  { k: "oil_gas", label: "Grey Wolfe Limited enquiries go to" },
  { k: "realtors", label: "Wolfe Realtors enquiries go to" },
  { k: "development", label: "Grey Wolfe Development enquiries go to" },
];

export const NotifyPanel = () => {
  const [form, setForm] = useState(null);
  useEffect(() => { http.get("/notify-settings").then((r) => setForm(r.data)); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(NOTIFY_FIELDS.map(({ k }) => [k, form[k]?.trim() || null]));
      const { data } = await http.put("/notify-settings", payload);
      setForm(data);
      toast.success("Enquiry emails saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  if (!form) return null;
  return (
    <form onSubmit={save} className="max-w-3xl mb-10 pb-8 border-b border-gw-ground/15" data-testid="admin-notify-form">
      <p className="lux text-2xl">Enquiry emails</p>
      <p className="text-sm text-gw-ground/70 mt-1 mb-5">Each new enquiry is emailed to the inbox of the company it was sent to. Leave blank to keep enquiries in this dashboard only.</p>
      <div className="grid sm:grid-cols-3 gap-5">
        {NOTIFY_FIELDS.map((f) => (
          <div key={f.k}>
            <Label htmlFor={`notify-${f.k}`} className="lab text-gw-ground text-[11px]">{f.label}</Label>
            <Input id={`notify-${f.k}`} type="email" value={form[f.k] || ""} placeholder="name@company.com"
              onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="mt-2 rounded-none" data-testid={`admin-notify-${f.k}-input`} />
          </div>
        ))}
      </div>
      <button type="submit" className="mt-5 lab bg-gw-ground text-white px-5 py-3 hover:bg-gw-deep transition-colors duration-200" data-testid="admin-notify-save-button">Save enquiry emails</button>
    </form>
  );
};

const EMAIL_STATUS = {
  sent: "Emailed",
  not_configured: "No inbox set",
  failed: "Email failed",
  paused: "Email paused",
  pending: "Sending",
};

const FIELDS = [
  { k: "whatsapp_number", label: "WhatsApp — Grey Wolfe Limited & Development (with country code)", placeholder: "+234 800 000 0000" },
  { k: "whatsapp_realtors", label: "WhatsApp — Wolfe Realtors (with country code)", placeholder: "+234 800 000 0000" },
  { k: "phone", label: "Phone shown on site" },
  { k: "email", label: "Email shown on site" },
  { k: "address", label: "Office address" },
];

const LogoSlot = ({ label, hint, value, onChange, testId }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await http.post("/upload", fd);
      onChange(data.path);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div>
      <Label className="lab text-gw-ground">{label}</Label>
      <p className="text-xs text-gw-ground/60 mt-1">{hint}</p>
      <div className="mt-2 h-24 bg-gw-ground flex items-center justify-center p-4" data-testid={`${testId}-preview`}>
        {value ? <img src={imgSrc(value)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="lab text-gw-mist text-[10px]">Default mark</span>}
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testId}-upload-button`}
          className="lab text-[11px] inline-flex items-center gap-2 border border-gw-ground/30 px-3 py-2 hover:bg-gw-tint transition-colors duration-200 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}{value ? "Replace" : "Upload"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove-button`}
            className="lab text-[11px] border border-gw-ground/30 px-3 py-2 text-destructive hover:bg-red-50 transition-colors duration-200">Remove</button>
        )}
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file-input`} />
    </div>
  );
};

export const SettingsPanel = () => {
  const { settings, refresh } = useSite();
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const save = async (e) => {
    e.preventDefault();
    try {
      await http.put("/settings", form);
      await refresh();
      toast.success("Details saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-5 max-w-3xl" data-testid="admin-settings-form">
      <div className="sm:col-span-2 border-b border-gw-ground/15 pb-6">
        <p className="lux text-2xl">Logo</p>
        <p className="text-sm text-gw-ground/70 mt-1 mb-4">Shown in the site header on a teal background. A PNG with a transparent background (or white/light artwork) works best.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <LogoSlot label="Main logo" hint="Used in light mode (and dark mode if no dark version)." value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} testId="admin-logo" />
          <LogoSlot label="Dark mode logo (optional)" hint="Used when visitors view the site in dark mode." value={form.logo_dark} onChange={(v) => setForm({ ...form, logo_dark: v })} testId="admin-logo-dark" />
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Switch id="logo_show_name" checked={form.logo_show_name !== false} onCheckedChange={(v) => setForm({ ...form, logo_show_name: v })} data-testid="admin-logo-show-name-switch" />
          <Label htmlFor="logo_show_name">Show “GREY WOLFE” text next to the logo (turn off if your logo already includes the name)</Label>
        </div>
      </div>
      {FIELDS.map((f) => (
        <div key={f.k}>
          <Label htmlFor={f.k} className="lab text-gw-ground">{f.label}</Label>
          <Input id={f.k} value={form[f.k] || ""} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
            className="mt-2 rounded-none" data-testid={`admin-settings-${f.k}-input`} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <button type="submit" className="lab bg-gw-ground text-white px-5 py-3 hover:bg-gw-deep transition-colors duration-200" data-testid="admin-settings-save-button">Save details</button>
      </div>
    </form>
  );
};
